import { type ReactNode, useLayoutEffect } from 'react'
import { Appearance, Platform } from 'react-native'
import { useColorScheme } from 'nativewind'
import { useThemeStore } from '../lib/stores/theme.store'

interface ThemeProviderProps {
  children: ReactNode
}

/**
 * Syncs the Zustand resolvedTheme into NativeWind's class engine.
 * Uses useLayoutEffect to set the scheme BEFORE the browser paints —
 * eliminates visible theme flicker on boot and navigation.
 */
export function TagoThemeProvider({ children }: ThemeProviderProps) {
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme)
  const { setColorScheme } = useColorScheme()

  useLayoutEffect(() => {
    setColorScheme(resolvedTheme)

    // Y el chrome NATIVO, que NativeWind no toca.
    //
    // NativeWind pinta lo que dibuja React. El teclado, los `Alert`, los action
    // sheets, los selectores de fecha y los menús contextuales los dibuja iOS, y
    // seguían la ventana. Como el tema de Tago es un interruptor in-app y no el
    // ajuste del SO, alguien con la app en oscuro veía un teclado blanco encima.
    //
    // `Appearance.setColorScheme` es el override a nivel ventana
    // (react-native/React/CoreModules/RCTAppearance.mm: `window.overrideUserInterfaceStyle`).
    // Solo funciona con `UIUserInterfaceStyle: Automatic` en el Info.plist; por
    // eso `ios.userInterfaceStyle` pasó a 'automatic' en app.config.ts.
    //
    // POR QUÉ ESTO NO ROMPE LA LECTURA DEL TEMA DEL SISTEMA, que sería el riesgo
    // obvio: escribir acá hace que `Appearance.getColorScheme()` devuelva NUESTRO
    // valor y no el del SO, así que `lib/theme/system-theme.ts` pasaría a
    // reportarse a sí mismo. No importa, y se comprobó: `ThemeMode` es
    // `'light' | 'dark'` --no hay modo "sistema"-- y `resolveTheme(mode, _systemTheme)`
    // devuelve `mode` ignorando el segundo argumento. Nada del producto decide
    // nada a partir del tema del SO. El día que exista un modo "sistema" esto hay
    // que revisarlo: ver la nota en lib/theme/system-theme.ts.
    //
    // SOLO iOS, Y LA RAZÓN QUE ESTABA ESCRITA ACÁ ERA FALSA.
    //
    // Decía que en Android "el manifiesto sigue en 'light', así que el override
    // no tendría con qué alinearse". Eso no describe ningún mecanismo real:
    // `Appearance.setColorScheme` en Android no toca el manifiesto ni una
    // ventana, es `AppCompatDelegate.setDefaultNightMode(MODE_NIGHT_YES/NO)`
    // (react-native/ReactAndroid/.../appearance/AppearanceModule.kt:55-64).
    // Nada ahí depende de `UIUserInterfaceStyle`, que es una llave de iOS.
    //
    // La razón verdadera para no encenderlo hoy es otra, y es de producto:
    // `setDefaultNightMode` solo cambia lo que se pinta si el tema de la
    // actividad hereda de DayNight, y con `userInterfaceStyle: 'light'` en la
    // raíz de app.config.ts el prebuild genera un `AppTheme` que NO lo es. O
    // sea: encenderlo tal cual no arregla nada, y arreglarlo de verdad es
    // cambiar la raíz a 'automatic' --un cambio NATIVO, que mueve el
    // fingerprint y solo llega con un build de tienda, sobre una app Android
    // que ya está viva con gente real.
    //
    // Así que va en su propio cambio nativo, deliberado y verificado en un
    // emulador, no de refilón en un commit de iOS. Que quede claro que es una
    // decisión de secuencia y no una imposibilidad de la plataforma.
    if (Platform.OS === 'ios') {
      Appearance.setColorScheme(resolvedTheme)
    }
  }, [resolvedTheme, setColorScheme])

  return <>{children}</>
}
